import { lazy, Suspense, useEffect, useState } from "react";
import { Button, Modal } from "antd";

import "./offerModal.css";

function getIsDarkTheme() {
  try {
    const savedTheme = localStorage.getItem("isDark");

    if (savedTheme !== null) {
      return JSON.parse(savedTheme) === true || savedTheme === "true";
    }
  } catch (_) {
    return localStorage.getItem("isDark") === "true";
  }

  return (
    document.body.classList.contains("dark") ||
    document.body.classList.contains("uprasti-auth-dark") ||
    document.documentElement.getAttribute("data-theme") === "dark"
  );
}

const inlineOfferModalStyles = `
  .offerModalTrigger {
    display: inline;
    border: 0;
    background: transparent;
    padding: 0;
    color: #3b79f6;
    font: inherit;
    font-weight: 650;
    cursor: pointer;
    text-decoration: underline;
    text-underline-offset: 3px;
  }

  .offerModalRoot .ant-modal-content {
    max-height: calc(100dvh - 28px);
    display: flex;
    flex-direction: column;
    border-radius: 26px;
    overflow: hidden;
    padding: 0;
    background: #f7f9ff;
    box-shadow: 0 30px 90px rgba(15, 23, 42, 0.24);
  }

  .offerModalRoot .ant-modal-header {
    flex: 0 0 auto;
    margin: 0;
    padding: 22px 26px 14px;
    border-bottom: 1px solid rgba(25, 73, 160, 0.1);
    background: rgba(255, 255, 255, 0.92);
  }

  .offerModalRoot .ant-modal-body {
    min-height: 0;
    padding: 0;
    overflow: hidden;
  }

  .offerModalRoot .ant-modal-close {
    top: 16px;
    right: 16px;
    border-radius: 14px;
    color: rgba(23, 32, 51, 0.6);
  }

  .offerModalRoot .ant-modal-close:hover {
    background: #eef3ff;
    color: #3b79f6;
  }

  .offerModalTitle {
    display: block;
    max-width: calc(100% - 48px);
    color: #172033;
    font-size: 24px;
    line-height: 1.18;
    font-weight: 850;
    letter-spacing: -0.035em;
  }

  .offerModalBody {
    height: min(720px, calc(100dvh - 112px));
    min-height: 0;
    display: flex;
    flex-direction: column;
    gap: 14px;
    padding: 18px;
    overflow: hidden;
  }

  .offerPdfViewerBox {
    flex: 1;
    min-height: 0;
    width: 100%;
    border-radius: 20px;
    border: 1px solid rgba(25, 73, 160, 0.1);
    background: #ffffff;
    overflow: auto;
    box-shadow: 0 16px 42px rgba(23, 32, 51, 0.08);
  }

  .offerPdfViewerBox .rpv-core__viewer {
    height: 100%;
  }

  .offerPdfViewerBox .rpv-core__inner-pages {
    padding: 18px 0;
  }

  .offerPdfViewerBox .rpv-core__inner-page {
    background: transparent;
  }

  .offerModalFooter {
    flex: 0 0 auto;
    display: flex;
    justify-content: flex-end;
  }

  .offerModalFooter .ant-btn {
    height: 44px;
    border-radius: 14px;
    padding: 0 22px;
    font-size: 14px;
    font-weight: 800;
    background: #3b79f6;
  }

  .offerModalRootDark .ant-modal-content {
    background: linear-gradient(180deg, #0f172a 0%, #0b1220 100%) !important;
    border: 1px solid rgba(148, 163, 184, 0.16) !important;
    box-shadow: 0 32px 100px rgba(2, 8, 23, 0.62) !important;
  }

  .offerModalRootDark .ant-modal-header {
    background: rgba(15, 23, 42, 0.96) !important;
    border-bottom: 1px solid rgba(148, 163, 184, 0.14) !important;
  }

  .offerModalRootDark .ant-modal-body {
    background: transparent !important;
  }

  .offerModalRootDark .ant-modal-close {
    color: #cbd5e1 !important;
  }

  .offerModalRootDark .ant-modal-close:hover {
    background: rgba(59, 121, 246, 0.16) !important;
    color: #ffffff !important;
  }

  .offerModalRootDark .offerModalTitle {
    color: #f8fafc !important;
  }

  .offerModalRootDark .offerPdfViewerBox {
    background: #0f172a !important;
    border-color: rgba(148, 163, 184, 0.16) !important;
    box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.03) !important;
  }

  .offerModalRootDark .rpv-core__viewer {
    background: #0f172a !important;
  }

  .offerModalRootDark .rpv-core__inner-pages {
    background: #0f172a !important;
  }

  .offerModalRootDark .rpv-core__inner-page {
    background: #0f172a !important;
  }

  .offerModalRootDark .rpv-core__canvas-layer canvas {
    filter: invert(1) hue-rotate(180deg) contrast(0.95) brightness(1.05);
    border-radius: 10px;
  }

  .offerModalRootDark .offerModalFooter .ant-btn {
    box-shadow: 0 10px 24px rgba(59, 121, 246, 0.26) !important;
  }

  @media (max-width: 560px) {
    .offerModalRoot .ant-modal-content {
      border-radius: 22px;
    }

    .offerModalRoot .ant-modal-header {
      padding: 20px 18px 12px;
    }

    .offerModalTitle {
      font-size: 21px;
    }

    .offerModalBody {
      height: calc(100dvh - 104px);
      padding: 12px;
    }

    .offerPdfViewerBox {
      border-radius: 16px;
    }

    .offerModalFooter .ant-btn {
      width: 100%;
    }
  }
`;

// Просмотрщик PDF (pdf.js) тяжёлый, а окно оферты есть на лендинге: грузим его только при открытии.
const PdfOnlyViewer = lazy(() => import("./OfferPdfViewer"));

export default function OfferModal({
  nameText = "Оферта",
  textInModal,
  ModalHeaderText = "",
}) {
  const [open, setOpen] = useState(false);
  const [isDark, setIsDark] = useState(getIsDarkTheme);

  useEffect(() => {
    const syncTheme = () => {
      setIsDark(getIsDarkTheme());
    };

    syncTheme();

    window.addEventListener("storage", syncTheme);

    const observer = new MutationObserver(syncTheme);

    observer.observe(document.body, {
      attributes: true,
      attributeFilter: ["class"],
    });

    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["class", "data-theme"],
    });

    return () => {
      window.removeEventListener("storage", syncTheme);
      observer.disconnect();
    };
  }, []);

  const modalClassName = `offerModalRoot ${
    isDark ? "offerModalRootDark" : "offerModalRootLight"
  }`;

  return (
    <>
      <style>{inlineOfferModalStyles}</style>

      <button
        type="button"
        className="offerModalTrigger"
        onClick={() => setOpen(true)}
      >
        {nameText}
      </button>

      <Modal
        open={open}
        onCancel={() => setOpen(false)}
        footer={null}
        centered
        width="min(980px, calc(100vw - 24px))"
        title={
          <span className="offerModalTitle">
            {ModalHeaderText || nameText}
          </span>
        }
        className={modalClassName}
        rootClassName={modalClassName}
        destroyOnClose
        maskStyle={{
          background: isDark
            ? "rgba(2, 8, 23, 0.78)"
            : "rgba(15, 23, 42, 0.45)",
        }}
      >
        <div className="offerModalBody">
          <div className="offerPdfViewerBox">
            {open && textInModal ? (
              <Suspense fallback={null}>
                <PdfOnlyViewer fileUrl={textInModal} isDark={isDark} />
              </Suspense>
            ) : null}
          </div>

          <div className="offerModalFooter">
            <Button type="primary" onClick={() => setOpen(false)}>
              Закрыть
            </Button>
          </div>
        </div>
      </Modal>
    </>
  );
}